import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, PhoneOutgoing, PhoneMissed, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CallHistoryPage = ({ user }) => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCallHistory();
  }, []);

  const fetchCallHistory = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(`${API}/calls/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalls(response.data);
    } catch (error) {
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString();
  };

  const getStatusIcon = (call) => {
    if (call.status === 'missed') {
      return <PhoneMissed className="w-4 h-4 text-[#FF3366]" />;
    }
    if (call.call_type === 'outgoing') {
      return <PhoneOutgoing className="w-4 h-4 text-[#22C55E]" />;
    }
    return <Phone className="w-4 h-4 text-[#22C55E]" />;
  };

  const handleCallBack = async (call) => {
    if (!call.contact_id) {
      toast.error('Contact not available');
      return;
    }
    
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.post(`${API}/calls/initiate`, 
        { contact_id: call.contact_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Calling ${call.contact_name}...`);
      navigate(`/call/${response.data.channel_name}`);
    } catch (error) {
      toast.error('Failed to initiate call');
    }
  };

  return (
    <div className="min-h-screen bg-black/40 text-white">
      {/* Header */}
      <nav className="border-b border-white/[0.08] bg-white/[0.03]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <button className="p-2 hover:bg-[#1E1E24] rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          </Link>
          <h1 className="text-xl font-bold">Call History</h1>
        </div>
      </nav>

      {/* Call History List */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center text-[#A1A1AA] py-20">Loading call history...</div>
        ) : calls.length === 0 ? (
          <div className="text-center py-20">
            <Clock className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-[#A1A1AA] mb-4">No calls yet</p>
            <Link to="/contacts">
              <button className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full font-semibold hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all">
                Call a contact
              </button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {calls.map((call) => (
              <div
                key={call.id}
                data-testid={`call-history-${call.id}`}
                className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/[0.06] rounded-full flex items-center justify-center">
                    {call.contact_name ? (
                      <span className="text-lg font-bold text-[#D4AF37]">
                        {call.contact_name.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <Phone className="w-5 h-5 text-[#A1A1AA]" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(call)}
                      <p className="font-medium">{call.contact_name || 'Unknown'}</p>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                      <span>{formatTime(call.started_at)}</span>
                      {call.duration_seconds && (
                        <span>{formatDuration(call.duration_seconds)}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {call.contact_id && (
                  <button
                    onClick={() => handleCallBack(call)}
                    data-testid={`callback-${call.id}`}
                    className="p-3 bg-[#22C55E] rounded-full hover:bg-[#16A34A] transition-all"
                  >
                    <Phone className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallHistoryPage;
